import React from 'react';
import Card from '../../components/Card';
import { parseCardTriggers } from '../../engine/triggerEngine';
import { TRIGGER_TYPES } from '../../engine/triggerTypes';
import { useBattle } from '../context';
import type { CardWithId } from '../../types/card';

interface GraveyardViewerModalProps {
  activateGraveyardCard: (card: CardWithId) => void;
}

/**
 * GraveyardViewerModal
 *
 * 墓地カード閲覧・選択モーダル
 * - 墓地のカードを一覧表示
 * - 墓地選択モード時はカードを選択可能
 * - 墓地発動可能なカードには発動ボタンを表示
 *
 * Props:
 * - activateGraveyardCard: 墓地カード発動コールバック
 */
const GraveyardViewerModal: React.FC<GraveyardViewerModalProps> = ({
  activateGraveyardCard,
}) => {
  // Context から状態を取得
  const {
    // 表示状態
    showGraveyardViewer,
    setShowGraveyardViewer,
    // 墓地データ
    p1Graveyard,
    p2Graveyard,
    // 墓地選択状態
    pendingGraveyardSelection,
    setPendingGraveyardSelection,
    pendingGraveyardSelectedCard,
    setPendingGraveyardSelectedCard,
    // ゲーム状態
    currentPlayer,
    phase,
    p1ActiveSP,
    p2ActiveSP,
    p1Field,
    p2Field,
    // カード詳細表示
    setMobileCardDetail,
  } = useBattle();
  // showGraveyardViewer が null の場合はレンダリングしない
  if (!showGraveyardViewer) {
    return null;
  }

  // 対象プレイヤーの墓地を取得
  const graveyard: CardWithId[] = showGraveyardViewer === 1 ? p1Graveyard : p2Graveyard;

  // モーダルを閉じる
  const handleClose = () => {
    setShowGraveyardViewer(null);
    setPendingGraveyardSelectedCard(null);
  };

  // 墓地選択を確定
  const handleConfirmSelection = () => {
    if (pendingGraveyardSelection?.callback && pendingGraveyardSelectedCard) {
      pendingGraveyardSelection.callback([pendingGraveyardSelectedCard]);
    }
    setPendingGraveyardSelection(null);
    setPendingGraveyardSelectedCard(null);
    setShowGraveyardViewer(null);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '2px solid #444',
          boxShadow: '0 0 30px rgba(0,0,0,0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: showGraveyardViewer === 1 ? '#4da6ff' : '#ff6b6b' }}>
            💀 {showGraveyardViewer === 1 ? 'プレイヤー1' : 'プレイヤー2'}の墓地
            ({graveyard.length}枚)
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: '#ff4444',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            ✕ 閉じる
          </button>
        </div>

        {/* 墓地選択モード中のメッセージ */}
        {pendingGraveyardSelection && (
          <div style={{
            background: 'rgba(156, 107, 255, 0.2)',
            border: '1px solid #9c6bff',
            borderRadius: '8px',
            padding: '10px',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#9c6bff', fontWeight: 'bold', marginBottom: '4px' }}>
              {pendingGraveyardSelection.message}
            </div>
            {pendingGraveyardSelectedCard && (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                <button
                  onClick={handleConfirmSelection}
                  style={{
                    background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  ✓ 【{pendingGraveyardSelectedCard.name}】を選択
                </button>
                <button
                  onClick={() => setPendingGraveyardSelectedCard(null)}
                  style={{
                    background: 'linear-gradient(135deg, #666, #888)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  選択解除
                </button>
              </div>
            )}
          </div>
        )}

        {/* カード一覧 */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          overflowY: 'auto',
          maxHeight: '60vh',
          padding: '8px',
          justifyContent: 'center',
        }}>
          {graveyard.length === 0 ? (
            <div style={{ color: '#888', padding: '40px', textAlign: 'center' }}>
              墓地にカードがありません
            </div>
          ) : (
            graveyard.map((card, index) => {
              const isSelectable = pendingGraveyardSelection
                ? (!pendingGraveyardSelection.filter || pendingGraveyardSelection.filter(card))
                : false;
              const isSelected = pendingGraveyardSelectedCard?.uniqueId === card.uniqueId;

              // この墓地カードに発動可能なトリガーがあるか確認
              const graveyardTriggers = (() => {
                if (showGraveyardViewer !== currentPlayer) return [];
                if (phase !== 2) return []; // メインフェイズのみ
                const triggers = parseCardTriggers(card);
                return triggers.filter((t) => {
                  if (t.type !== TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD) return false;
                  if (t.costCheck) {
                    // costCheckは主にSPコストのチェックに使われるため、最小限のcontextを提供
                    const costCheckContext = {
                      currentPlayer,
                      p1ActiveSP,
                      p2ActiveSP,
                      addLog: () => {}, // costCheckでは使用されない
                    } as Parameters<typeof t.costCheck>[0];
                    if (!t.costCheck(costCheckContext)) return false;
                  }
                  // モンスターの場合、場に空きがあるか確認
                  if (card.type === 'monster') {
                    const field = currentPlayer === 1 ? p1Field : p2Field;
                    if (!field.some((slot) => slot === null)) return false;
                  }
                  return true;
                });
              })();
              const hasActivatableTrigger = graveyardTriggers.length > 0;

              // カード詳細を表示
              const showCardDetail = () => {
                setMobileCardDetail({ card, source: 'graveyard' });
              };

              return (
                <div
                  key={card.uniqueId || index}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    onClick={() => {
                      if (pendingGraveyardSelection && isSelectable) {
                        // 選択モード中：選択/解除
                        setPendingGraveyardSelectedCard(isSelected ? null : card);
                      } else if (!pendingGraveyardSelection) {
                        // 選択モードでない場合：カード詳細を表示
                        showCardDetail();
                      }
                    }}
                    style={{
                      cursor: (pendingGraveyardSelection && isSelectable) || !pendingGraveyardSelection ? 'pointer' : 'default',
                      border: isSelected ? '3px solid #9c6bff' :
                              hasActivatableTrigger ? '2px solid #ffd700' :
                              (pendingGraveyardSelection && !isSelectable) ? '2px solid #444' : 'none',
                      borderRadius: '8px',
                      padding: '2px',
                      opacity: (pendingGraveyardSelection && !isSelectable) ? 0.5 : 1,
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Card card={card} small />
                  </div>
                  {/* ボタン群 */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {/* 選択モード中の詳細表示ボタン */}
                    {pendingGraveyardSelection && (
                      <button
                        onClick={showCardDetail}
                        style={{
                          background: 'linear-gradient(135deg, #2196f3, #42a5f5)',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        🔍 詳細
                      </button>
                    )}
                    {/* 墓地発動ボタン */}
                    {hasActivatableTrigger && !pendingGraveyardSelection && (
                      <button
                        onClick={() => {
                          activateGraveyardCard(card);
                          setShowGraveyardViewer(null);
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #6b4e9e, #8b6bb8)',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        💀 発動
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default GraveyardViewerModal;
